import { inject, Injectable } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import { Auth, EmailAuthProvider, GoogleAuthProvider, User, UserCredential } from '@angular/fire/auth';
import { applyActionCode, authState, browserLocalPersistence, browserSessionPersistence, confirmPasswordReset, createUserWithEmailAndPassword, idToken, linkWithCredential, sendEmailVerification, sendPasswordResetEmail, signInAnonymously, signInWithEmailAndPassword, signInWithPopup, updateProfile, user, verifyPasswordResetCode } from '@angular/fire/auth';
import { Observable } from 'rxjs';

import { AuthError, AuthErrorCode } from './utils/error';

/**
 * Type representing user profile information that can be updated
 */
export type UserProfile = { displayName?: string | null, photoURL?: string | null };

/**
 * Service responsible for managing authentication operations and user state
 * Handles user authentication, registration, profile updates, and password management
 */
@Injectable({
  providedIn: 'root'
})
export class AuthCoreService {

  //#region Authentication Materials
  /**
   * Firebase Authentication instance
   * Used for all authentication operations
   */
  protected readonly auth: Auth = inject(Auth);
  //#endregion

  //#region Authentication Properties
  get currentUser(): User | null {
    return this.auth.currentUser;
  }

  /** The observable streams events triggered by sign-in, sign-out, and token refresh events. */
  readonly user$: Observable<User | null> = user(this.auth);

  /** The observable streams events triggered by sign-in and sign-out events. */
  readonly authState$: Observable<User | null> = authState(this.auth);

  /** The observable streams events triggered by sign-in, sign-out and token refresh events. */
  readonly authToken$: Observable<string | null> = idToken(this.auth);
  //#endregion

  //#region Initialization
  /**
   * Initializes the authentication service with language preferences
   * Sets the default language code for authentication operations and UI elements
   * 
   * @param languageCode Optional language code (e.g. 'en', 'tr')
   */
  public initialize(languageCode?: string): void {
    // If a specific language code is provided, use it for Firebase Auth UI
    // Otherwise, detect and use the device language automatically
    languageCode ? this.auth.languageCode = languageCode : this.auth.useDeviceLanguage();

    // Return an observable that emits the first non-null user object
    // return this.user$.pipe(first(user => !!user));
  }
  //#endregion

  //#region Sign Methods
  /**
   * Authenticates a user with email and password credentials.
   * 
   * This method handles two distinct authentication flows:
   * 1. For anonymous users: Converts the anonymous account to a permanent one while preserving associated data
   * 2. For new/returning users: Standard email/password authentication
   * 
   * The authentication state persistence is configurable via the 'remember' parameter.
   * 
   * @param email The user's email address for authentication
   * @param password The user's password for authentication
   * @param remember When true, authentication persists across browser restarts; 
   *                 when false, it's cleared when the browser closes
   * @returns A Promise resolving to the authenticated User object
   * @throws AuthError with specific code for authentication failures
   */
  public async signIn(email: string, password: string, remember: boolean = false): Promise<User> {
    // Store reference to the current user before authentication attempt
    // This allows us to detect if we're upgrading from an anonymous session
    const currentUser = this.auth.currentUser;

    try {
      // Configure how long the authentication state should persist:
      // - browserLocalPersistence: Survives browser restarts (remember me)
      // - browserSessionPersistence: Cleared when browser is closed (default)
      await this.auth.setPersistence(remember ? browserLocalPersistence : browserSessionPersistence);

      // Attempt to sign in with the provided email and password
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);

      // If we had an anonymous user before, we need to handle data migration
      if (currentUser && currentUser.isAnonymous) {
        try {
          await this.migrateFromAnonymousUser(currentUser);
        } catch (migrationError) {
          console.error('Failed to migrate anonymous user data:', migrationError);
          // Continue with sign-in even if migration fails
          // The user is already authenticated at this point
        }
      }

      // Return the successfully authenticated user object
      return userCredential.user;
    } catch (error) {
      // Handle authentication errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }

  /**
   * Signs in a user anonymously.
   * 
   * This method creates a temporary anonymous user account that can later be upgraded
   * to a permanent account by linking credentials (e.g., email/password).
   * 
   * @returns A Promise resolving to the newly created anonymous User object
   * @throws AuthError with specific code for sign-in failures
   */
  public async signInAnonymously(): Promise<User> {
    try {
      // Attempt to sign in anonymously
      const userCredential = await signInAnonymously(this.auth);

      // Return the newly created anonymous user object
      return userCredential.user;
    }
    catch (error) {
      // Handle sign-in errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }

  /**
   * Sign in a user with Google authentication.
   * 
   * This method uses the GoogleAuthProvider to initiate a sign-in flow
   * that allows users to authenticate using their Google account.
   * 
   * @returns A Promise resolving to the user object after signing in with Google
   */
  public async signInWithGoogle(): Promise<User> {
    const provider = new GoogleAuthProvider();
    try {
      // Use GoogleAuthProvider to initiate sign-in with Google
      // This will open a popup for the user to authenticate with their Google account.
      const userCredential = await signInWithPopup(this.auth, provider);
      
      // Return the user object from the sign-in result
      return userCredential.user;
    } catch (error) {
      // Handle sign-in errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }

  /**
   * Signs out the currently authenticated user.
   * After sign-out, automatically signs in anonymously due to the subscription in constructor.
   * 
   * @returns A Promise that resolves when sign-out is complete
   * @throws AuthError if sign-out fails
   */
  public async signOut(): Promise<void> {
    try {
      // Sign out the current user by clearing the authentication state
      await this.auth.signOut();
      // The authState subscription in the constructor will detect the null auth state
      // and automatically create a new anonymous user for continued usage
    } catch (error) {
      // Handle sign-out errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }

  /**
   * Creates a new user account with email and password authentication.
   * 
   * This method handles two registration scenarios:
   * 1. Converting an existing anonymous account to a permanent one
   * 2. Creating an entirely new account when no user session exists
   * 
   * After successful account creation, an email verification is automatically sent.
   * 
   * @param email The email address to register with the new account
   * @param password The password to set for the new account
   * @returns A Promise resolving to the newly created User object
   * @throws AuthError with specific code for registration failures
   */
  public async signUp(email: string, password: string): Promise<User> {
    // Get current user to check if we're converting an anonymous account
    const currentUser = this.auth.currentUser;

    try {
      // Variable to store the resulting user credentials from either flow
      let userCredential: UserCredential;

      // ANONYMOUS USER UPGRADE FLOW:
      if (currentUser && currentUser.isAnonymous) {
        // Create an email/password credential object from the provided information
        // This credential will be linked to the existing anonymous account
        const newCredential = EmailAuthProvider.credential(email, password);

        // Link the new credential to the anonymous user
        // This preserves the user ID and any associated data from the anonymous session while upgrading to a permanent authenticated account
        userCredential = await linkWithCredential(currentUser, newCredential);
      }
      // NEW ACCOUNT CREATION FLOW:
      else {
        // No existing anonymous user, create a completely new account
        // This generates a new user ID and creates a fresh user record
        userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      }

      // Send verification email to confirm the user owns this email address
      // This is a security best practice to prevent account takeover attempts
      await sendEmailVerification(userCredential.user);

      // Return the newly created or upgraded user object
      return userCredential.user;
    } catch (error) {
      // Handle registration errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }
  //#endregion

  //#region User Methods
  /**
   * Migrates user data from an anonymous account to an authenticated account
   * This helper method handles the process of transferring any user data and
   * cleaning up the anonymous account after successful authentication
   * 
   * @param anonymousUser The anonymous user account to migrate from
   * @returns A Promise that resolves when migration is complete
   * @throws AuthError if migration fails or if no authenticated user is found
   */
  private async migrateFromAnonymousUser(anonymousUser: User): Promise<void> {
    // // Get the currently authenticated user after sign-in
    // // This should be the new non-anonymous user account
    // const user = this.auth.currentUser;

    // // Verify that we have an authenticated user before proceeding
    // if (user === null || user.isAnonymous) {
    //   throw new AuthError('Authenticated user not found.');
    // }

    // try {
    //   // Fetch all user-related data associated with the anonymous account
    //   // This could include preferences, cart items, or other app-specific data
    //   const anonymousUserData = await this.userRepository.getData(anonymousUser);

    //   // Only proceed with data migration if there's actually data to migrate
    //   if (anonymousUserData) {
    //     // Copy all data from the anonymous account to the authenticated account
    //     // This ensures a seamless transition from anonymous to authenticated state
    //     // and prevents loss of user progress or preferences
    //     await this.userRepository.addData(user, anonymousUserData);
    //   }

    //   // After successful data migration, remove the anonymous account
    //   // This is important for database cleanup and security
    //   // It prevents orphaned anonymous accounts from accumulating in the system
    //   await anonymousUser.delete();
    // } catch (error) {
    //   // Handle migration errors with proper error transformation
    //   if (error instanceof FirebaseError) {
    //     // Convert Firebase errors to our application's error format
    //     throw new AuthError(error.code);
    //   }

    //   // Rethrow any non-Firebase errors
    //   throw error;
    // }
  }

  /**
   * Updates the profile information of the specified user.
   * Can modify displayName and/or photoURL properties.
   * 
   * @param user The user whose profile should be updated
   * @param profile The profile information to update (displayName and/or photoURL)
   * @returns A Promise that resolves when the profile update is complete
   * @throws AuthError if profile update fails
   */
  public async updateProfile(user: User, profile: UserProfile): Promise<void> {
    try {
      // Update the user's profile information with Firebase Auth
      // Both displayName and photoURL are optional fields
      await updateProfile(user, profile);
    } catch (error) {
      // Handle profile update errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }
  //#endregion

  //#region Confirm Methods
  /**
   * Completes the password reset process, given a confirmation token and new password.
   * 
   * @param token The password reset token received via email
   * @param newPassword The new password to set
   * @returns A Promise that resolves when the password reset is complete
   * @throws AuthError if password reset confirmation fails
   */
  public async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    try {
      // Use Firebase Auth to validate the password reset code and set the new password
      // This completes the password reset flow initiated by sendPasswordResetEmail
      await confirmPasswordReset(this.auth, token, newPassword);
    } catch (error) {
      // Handle password reset errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }
  //#endregion

  //#region Send Email Methods
  /**
   * Sends an email verification to the currently authenticated user.
   * 
   * @param continueUrl Optional URL to redirect user after verifying email.
   * Must be a complete URL including protocol (e.g., 'https://www.example.com/verified')
   * @returns A Promise that resolves when the email has been sent
   * @throws AuthError if the user is not authenticated or if sending fails
   */
  public async sendEmailVerification(continueUrl?: string): Promise<void> {
    // Retrieve the current user from Firebase Auth
    const user = this.auth.currentUser;

    // Verify that we have an authenticated user before proceeding
    // If no user is found, throw a custom error
    if (user === null) {
      throw new AuthError(AuthErrorCode.USER_NOT_FOUND);
    }

    try {
      // If continueUrl is provided, create an actionCodeSettings object
      // This determines where the user is redirected after verifying their email
      const actionCodeSettings = continueUrl
        ? { url: continueUrl } // Specify the redirect URL
        : undefined;           // Use default Firebase behavior

      // Send the verification email to the current user's email address
      await sendEmailVerification(user, actionCodeSettings);
    } catch (error) {
      // Handle verification email errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }

  /**
   * Sends a password reset email to the specified email address.
   * 
   * @param email The email address to send the password reset email to
   * @param continueUrl Optional URL to redirect user after resetting password.
   * Must be a complete URL including protocol (e.g., 'https://www.example.com/reset-success')
   * @returns A Promise that resolves when the email has been sent
   * @throws AuthError if sending the password reset email fails
   */
  public async sendPasswordResetEmail(email: string, continueUrl?: string): Promise<void> {
    try {
      // If continueUrl is provided, create an actionCodeSettings object
      // This determines where the user is redirected after resetting their password
      const actionCodeSettings = continueUrl
        ? { url: continueUrl } // Specify the redirect URL
        : undefined;           // Use default Firebase behavior

      // Send the password reset email to the specified email address
      await sendPasswordResetEmail(this.auth, email, actionCodeSettings);
    } catch (error) {
      // Handle password reset email errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }
  //#endregion

  //#region Verification Methods
  /**
   * Verifies a user's email address using a verification token sent to their email.
   * This completes the email verification process initiated by sendEmailVerification.
   * 
   * @param token The email verification token received via email
   * @returns A Promise that resolves when email verification is complete
   * @throws AuthError if the verification token is invalid or expired
   */
  public async verifyEmailVerificationToken(token: string): Promise<void> {
    try {
      await applyActionCode(this.auth, token);
    } catch (error) {
      // Handle verification errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }

  /**
   * Verifies a password reset token sent to user by email or other out-of-band mechanism.
   * Use this to validate the token before showing password reset UI to the user.
   * 
   * @param token The password reset token to verify
   * @returns A Promise that resolves to the user email address associated with the token if valid
   * @throws AuthError if the token is invalid or expired
   */
  public async verifyPasswordResetToken(token: string): Promise<string> {
    try {
      // Check with Firebase Auth whether the reset code is valid
      // This is typically used before showing the password reset form to the user
      // Returns the email address associated with the code if valid
      return await verifyPasswordResetCode(this.auth, token);
    } catch (error) {
      // Handle verification errors with proper error transformation
      if (error instanceof FirebaseError) {
        // Convert Firebase errors to our application's error format
        throw new AuthError(error.code);
      }

      // Rethrow any non-Firebase errors
      throw error;
    }
  }
  //#endregion
}
